import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, Download, TrendingUp, TrendingDown, DollarSign, BarChart3 } from 'lucide-react';
import { useThemeStore } from '../../stores/theme.store';
import { useConsolidatedReport, useExportReport } from '../../hooks/useFinancialReports';
import type { ConsolidatedReportFilters } from '../../hooks/useFinancialReports';

type PresetPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

function getPresetDates(preset: PresetPeriod): { startDate: string; endDate: string } {
  const now = new Date();
  const end = now.toISOString().split('T')[0];
  let start: Date;
  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      start = new Date(now);
      start.setDate(now.getDate() - 7);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'quarter':
      start = new Date(now);
      start.setMonth(now.getMonth() - 3);
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
  }
  return { startDate: start.toISOString().split('T')[0], endDate: end };
}

function fmtBRL(centavos: number | undefined | null): string {
  if (centavos === null || centavos === undefined) return 'R$ 0,00';
  return `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ConsolidatedReportPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeStore();
  const [preset, setPreset] = useState<PresetPeriod>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [operationType, setOperationType] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMutation = useExportReport();

  const dates = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    return getPresetDates(preset);
  }, [preset, customStart, customEnd]);

  const filters: ConsolidatedReportFilters = useMemo(() => ({
    startDate: dates.startDate,
    endDate: dates.endDate,
    groupBy,
    ...(operationType ? { operationType } : {}),
  }), [dates, groupBy, operationType]);

  const { data, isLoading } = useConsolidatedReport(filters);

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const summary = data?.summary;
  const netProfitPositive = (summary?.netProfit ?? 0) >= 0;

  const handleExport = (format: 'PDF' | 'CSV' | 'XLSX') => {
    setShowExportMenu(false);
    exportMutation.mutate({ reportType: 'consolidated', format, filters });
  };

  const chartData = useMemo(() => {
    if (!data?.timeSeries) return [];
    return data.timeSeries.map((entry) => ({
      period: entry.period,
      Entradas: entry.inflow / 100,
      Saídas: entry.outflow / 100,
      Receita: entry.grossRevenue / 100,
      'Lucro Líquido': entry.netProfit / 100,
    }));
  }, [data?.timeSeries]);

  const revenueBarData = useMemo(() => {
    if (!data?.timeSeries) return [];
    return data.timeSeries.map((entry) => ({
      period: entry.period,
      Entradas: entry.inflow / 100,
      Saídas: entry.outflow / 100,
    }));
  }, [data?.timeSeries]);

  const revenueCompositionData = useMemo(() => {
    if (!summary?.revenueByType) return [];
    return [
      { name: 'Cash-In', value: summary.revenueByType.cashIn / 100 },
      { name: 'Cash-Out', value: summary.revenueByType.cashOut / 100 },
      { name: 'Mensalidades', value: summary.revenueByType.membership / 100 },
    ];
  }, [summary?.revenueByType]);

  // Skeleton loader
  if (isLoading) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-8 w-64 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`${cardBg} rounded-xl p-5 space-y-3`}>
                <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
                <div className="h-7 w-36 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
          <div className={`${cardBg} rounded-xl p-6 h-72 animate-pulse`} />
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || (!summary?.totalInflow && !summary?.totalOutflow && !summary?.grossRevenue)) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/admin/reports')} className={`flex items-center gap-2 mb-6 ${textSecondary} hover:${textPrimary} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className={`${cardBg} rounded-xl p-12 text-center`}>
            <BarChart3 className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
            <h2 className={`text-lg font-semibold ${textPrimary}`}>Nenhum dado encontrado</h2>
            <p className={`mt-2 ${textSecondary}`}>Não há dados financeiros para o período selecionado. Ajuste os filtros e tente novamente.</p>
          </div>
        </div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Volume Entradas', value: fmtBRL(summary?.totalInflow), color: 'text-green-500', icon: TrendingUp, iconBg: 'bg-green-500/10' },
    { label: 'Volume Saídas', value: fmtBRL(summary?.totalOutflow), color: 'text-red-500', icon: TrendingDown, iconBg: 'bg-red-500/10' },
    { label: 'Receita Bruta', value: fmtBRL(summary?.grossRevenue), color: 'text-blue-500', icon: DollarSign, iconBg: 'bg-blue-500/10' },
    { label: 'Custos Operacionais', value: fmtBRL(summary?.operationalCosts), color: 'text-orange-500', icon: BarChart3, iconBg: 'bg-orange-500/10' },
    { label: 'Lucro Líquido', value: fmtBRL(summary?.netProfit), color: netProfitPositive ? 'text-green-500' : 'text-red-500', icon: netProfitPositive ? TrendingUp : TrendingDown, iconBg: netProfitPositive ? 'bg-green-500/10' : 'bg-red-500/10' },
    { label: 'Margem de Lucro', value: `${(summary?.profitMargin ?? 0).toFixed(2)}%`, color: 'text-purple-500', icon: BarChart3, iconBg: 'bg-purple-500/10' },
  ];

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/reports')} className={`p-2 rounded-lg ${cardBg} border ${border} hover:opacity-80 transition-opacity`}>
              <ArrowLeft className={`w-4 h-4 ${textPrimary}`} />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>Relatório Financeiro Consolidado</h1>
              <p className={`text-sm ${textSecondary}`}>Visão executiva de receitas, custos e lucro</p>
            </div>
          </div>
          {/* Export button */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </button>
            {showExportMenu && (
              <div className={`absolute right-0 mt-2 w-40 rounded-lg shadow-lg border ${border} ${cardBg} z-10`}>
                {(['PDF', 'CSV', 'XLSX'] as const).map((fmt) => (
                  <button key={fmt} onClick={() => handleExport(fmt)} className={`block w-full text-left px-4 py-2 text-sm ${textPrimary} hover:bg-blue-500/10 first:rounded-t-lg last:rounded-b-lg`}>
                    {fmt === 'XLSX' ? 'Excel' : fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className={`${cardBg} rounded-xl p-4 border ${border}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Período</label>
              <select value={preset} onChange={(e) => setPreset(e.target.value as PresetPeriod)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="today">Hoje</option>
                <option value="week">Última Semana</option>
                <option value="month">Este Mês</option>
                <option value="quarter">Trimestre</option>
                <option value="year">Este Ano</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Início</label>
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Fim</label>
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
                </div>
              </>
            )}
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Tipo de Operação</label>
              <select value={operationType} onChange={(e) => setOperationType(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="">Todos</option>
                <option value="PIX_RECEIVE">PIX Recebido</option>
                <option value="PIX_SEND">PIX Enviado</option>
                <option value="TRANSFER_INTERNAL">Transferência</option>
                <option value="BOLETO">Boleto</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Agrupamento</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="day">Dia</option>
                <option value="week">Semana</option>
                <option value="month">Mês</option>
              </select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {kpiCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className={`${cardBg} rounded-xl p-5 border ${border}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${kpi.iconBg}`}>
                    <Icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                  <span className={`text-sm font-medium ${textSecondary}`}>{kpi.label}</span>
                </div>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            );
          })}
        </div>

        {/* Line Chart - Temporal Evolution */}
        {chartData.length > 0 && (
          <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
            <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Evolução Temporal</h2>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="period" tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <YAxis tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, borderRadius: 8 }}
                  labelStyle={{ color: isDarkMode ? '#fff' : '#111827' }}
                  formatter={(value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                />
                <Legend />
                <Line type="monotone" dataKey="Entradas" stroke="#22c55e" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Saídas" stroke="#ef4444" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Receita" stroke="#3b82f6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Lucro Líquido" stroke="#a855f7" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Stacked Bar Chart - Revenue Composition */}
        {revenueCompositionData.length > 0 && data?.timeSeries && data.timeSeries.length > 0 && (
          <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
            <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Composição da Receita por Tipo</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="period" tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                <YAxis tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, borderRadius: 8 }}
                  labelStyle={{ color: isDarkMode ? '#fff' : '#111827' }}
                  formatter={(value) => `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                />
                <Legend />
                <Bar dataKey="Entradas" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Saídas" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Revenue by Type Summary */}
        {summary?.revenueByType && (
          <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
            <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Receita por Tipo de Taxa</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Cash-In (PIX Recebido)</p>
                <p className={`text-lg font-bold text-green-500 mt-1`}>{fmtBRL(summary.revenueByType.cashIn)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Cash-Out (PIX Enviado)</p>
                <p className={`text-lg font-bold text-red-500 mt-1`}>{fmtBRL(summary.revenueByType.cashOut)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Mensalidades</p>
                <p className={`text-lg font-bold text-blue-500 mt-1`}>{fmtBRL(summary.revenueByType.membership)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
